// src/features/calendar/hooks/usePlacementMode.ts
// 放置模式狀態機（行動版）
// 從 Calendar.tsx 提取

import { useState, useCallback } from 'react';
import { Task } from '../../../types/task';
import { PlacementModeData, initialPlacementModeData } from '../../../types/calendar';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { logger } from '../../../utils/logger';
import { format } from 'date-fns';
import {
  buildAndSaveLocalTimeBox,
  addTimeBoxIdToTask,
  syncTimeBoxToFirestore,
} from '../utils/timeBoxCreation';

interface UsePlacementModeOptions {
  onRefresh: () => void;
  onError: (title: string, message: string) => void;
  setIsMobileTrayExpanded: (expanded: boolean) => void;
}

export function usePlacementMode({
  onRefresh,
  onError,
  setIsMobileTrayExpanded,
}: UsePlacementModeOptions) {
  const [placementMode, setPlacementMode] = useState<PlacementModeData>(initialPlacementModeData);

  /**
   * 進入放置模式（行動版）
   * 步驟 1：用戶點擊任務卡片，進入「選取」狀態
   */
  const handleEnterPlacementMode = useCallback((task: Task) => {
    setPlacementMode({
      state: 'selecting',
      selectedTask: task,
      previewSlot: null,
    });
    // 收合托盤以顯示窄列
    setIsMobileTrayExpanded(false);
  }, [setIsMobileTrayExpanded]);

  /**
   * 退出放置模式（行動版）
   * 回到 idle 狀態並恢復托盤
   */
  const handleExitPlacementMode = useCallback(() => {
    setPlacementMode(initialPlacementModeData);
    // 恢復托盤展開狀態
    setIsMobileTrayExpanded(true);
  }, [setIsMobileTrayExpanded]);

  /**
   * 選擇時段（放置模式步驟 2）
   * 用戶點擊日曆時段，進入「預覽」狀態
   */
  const handleSelectTimeSlot = useCallback((startTime: Date, endTime: Date) => {
    if (placementMode.state !== 'selecting' || !placementMode.selectedTask) return;

    setPlacementMode(prev => ({
      ...prev,
      state: 'previewing',
      previewSlot: { startTime, endTime },
    }));
  }, [placementMode.state, placementMode.selectedTask]);

  /**
   * 確認放置（放置模式步驟 3）
   * 建立 TimeBox 並退出放置模式
   *
   * Local-first 流程：
   * 1. 建立本地 TimeBox + 寫入 IndexedDB（樂觀更新）
   * 2. 更新 Task 的 scheduledTimeBoxIds
   * 3. 退出放置模式，觸發 UI 更新
   * 4. 背景同步到 Firestore
   */
  const handleConfirmPlacement = useCallback(async () => {
    if (placementMode.state !== 'previewing' || !placementMode.selectedTask || !placementMode.previewSlot) {
      return;
    }

    const task = placementMode.selectedTask;
    const { startTime, endTime } = placementMode.previewSlot;

    try {
      // 1. 建立本地 TimeBox（寫入 IndexedDB）
      const localTimeBox = await buildAndSaveLocalTimeBox(task, startTime, endTime);

      // 2. 更新 Task 的 scheduledTimeBoxIds 並同步 lastTimeboxedDate
      const lastTimeboxedDate = await addTimeBoxIdToTask(task.id, localTimeBox.id, format(localTimeBox.startTime, 'yyyy-MM-dd'));

      // 3. 退出放置模式，觸發 UI 更新
      setPlacementMode(initialPlacementModeData);
      eventBus.publish(EVENTS.TIMEBOX.CREATED, localTimeBox);
      onRefresh();

      // 4. 背景同步到 Firestore（傳入 lastTimeboxedDate 避免從 IndexedDB 重讀時的競爭條件）
      await syncTimeBoxToFirestore(localTimeBox, task, lastTimeboxedDate);
    } catch (error) {
      logger.error('Calendar', 'Placement mode: failed to create TimeBox', error);
      onError('Schedule Failed', 'Failed to create TimeBox. Please try again.');
    }
  }, [placementMode, onRefresh, onError]);

  /**
   * 取消預覽（放置模式）
   * 清除預覽，回到「選取」狀態
   */
  const handleCancelPreview = useCallback(() => {
    if (placementMode.state !== 'previewing') return;

    setPlacementMode(prev => ({
      ...prev,
      state: 'selecting',
      previewSlot: null,
    }));
  }, [placementMode.state]);

  return {
    placementMode,
    handleEnterPlacementMode,
    handleExitPlacementMode,
    handleSelectTimeSlot,
    handleConfirmPlacement,
    handleCancelPreview,
  };
}
